import { useCallback, useRef } from "react";
import { useLocation, useNavigate, useSearchParams } from "react-router";

/** The address parameter an in-page tab lives in (UI rules F-213). */
export const URL_TAB_PARAM = "tab";

export interface UrlTabWriteOptions {
  replace?: boolean;
  clearParams?: readonly string[];
}

export interface UrlTabState<Tab extends string> {
  /** The tab the address names, or the default. */
  tab: Tab;
  /** True when the address names a known tab explicitly. */
  explicit: boolean;
  /**
   * Writes a tab to the address; `replace` avoids a new history entry and
   * `clearParams` drops parameters the choice supersedes.
   */
  setTab: (tab: Tab, options?: UrlTabWriteOptions) => void;
  /** The same address for native links, including modified-click navigation. */
  getHref: (tab: Tab, options?: UrlTabWriteOptions) => string;
}

/**
 * F-213: every tab has its own address. The tab lives in `?tab=`, so back
 * returns to the previous tab, refresh keeps it and a copied link opens it.
 *
 * @remarks
 * `ids` maps each address value to the page's own tab value. An unknown or
 * missing value falls back to the default tab, whose address stays bare, so
 * opening a page never adds a history entry. Choosing a tab pushes one.
 * `setTab` keeps one identity for the page's life: react-router rebuilds
 * `setSearchParams` on every address change, and effects that depend on it
 * would otherwise re-run on Back and push again.
 */
export function useUrlTab<Tab extends string>(
  ids: Readonly<Record<string, Tab>>,
  fallback: Tab,
): UrlTabState<Tab> {
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const { pathname, hash } = useLocation();
  const raw = searchParams.get(URL_TAB_PARAM);
  const known =
    raw !== null && Object.prototype.hasOwnProperty.call(ids, raw) ? raw : null;
  const explicit = known !== null;
  const tab = known !== null ? ids[known] : fallback;
  const latest = useRef({
    fallback,
    ids,
    searchParams,
    navigate,
    pathname,
    hash,
  });
  latest.current = {
    fallback,
    ids,
    searchParams,
    navigate,
    pathname,
    hash,
  };
  const getSearch = useCallback((next: Tab, options?: UrlTabWriteOptions) => {
    const current = latest.current;
    const id = Object.keys(current.ids).find(
      (key) => current.ids[key] === next,
    );
    const params = new URLSearchParams(current.searchParams);
    for (const name of options?.clearParams ?? []) params.delete(name);
    if (next === current.fallback || id === undefined) {
      params.delete(URL_TAB_PARAM);
    } else {
      params.set(URL_TAB_PARAM, id);
    }
    return params;
  }, []);
  const setTab = useCallback(
    (next: Tab, options?: UrlTabWriteOptions) => {
      const params = getSearch(next, options);
      const current = latest.current;
      if (params.toString() === current.searchParams.toString()) return;
      const query = params.toString();
      void current.navigate(
        {
          pathname: current.pathname,
          search: query ? `?${query}` : "",
          hash: current.hash,
        },
        { replace: options?.replace },
      );
    },
    [getSearch],
  );
  const getHref = useCallback(
    (next: Tab, options?: UrlTabWriteOptions) => {
      const query = getSearch(next, options).toString();
      const current = latest.current;
      return `${current.pathname}${query ? `?${query}` : ""}${current.hash}`;
    },
    [getSearch],
  );
  return { tab, explicit, setTab, getHref };
}
